<template>
  <div class="stock-search-container">
    <div class="search-wrapper">
      <div class="search-input-group">
        <font-awesome-icon icon="fa-solid fa-search" class="search-icon" />
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          :placeholder="placeholder"
          class="stock-search-input"
          @input="handleInputCombined"
          @focus="showSuggestions = true"
          @blur="handleBlur"
          @keydown="handleKeydown"
          autocomplete="off"
        />
        <button v-if="searchQuery" @click="clearSearch" class="clear-btn">
          <font-awesome-icon icon="fa-solid fa-times" />
        </button>
      </div>

      <!-- Search Suggestions Dropdown -->
      <div
        v-if="showSuggestions && (suggestions.length > 0 || loading)"
        class="suggestions-dropdown"
      >
        <!-- Loading State -->
        <div v-if="loading" class="suggestion-item loading">
          <div class="loading-spinner"></div>
          <span>Searching stocks...</span>
        </div>

        <!-- Popular Stocks (when no search query) -->
        <div
          v-else-if="!searchQuery.trim() && popularStocks.length > 0"
          class="suggestions-section"
        >
          <div class="section-header">Popular Stocks</div>
          <div
            v-for="(stock, index) in popularStocks"
            :key="stock.symbol"
            :class="[
              'suggestion-item',
              { highlighted: highlightedIndex === index },
            ]"
            @mousedown="selectStock(stock)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="stock-info">
              <div class="stock-main">
                <span class="stock-symbol">{{ stock.symbol }}</span>
                <span class="stock-name">{{ stock.name }}</span>
              </div>
              <div class="stock-meta">
                <span class="stock-price">${{ formatPrice(stock.price) }}</span>
                <span
                  class="stock-change"
                  :class="getChangeClass(stock.change)"
                >
                  {{ formatChange(stock.change) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Search Results -->
        <div v-else-if="suggestions.length > 0" class="suggestions-section">
          <div class="section-header">Search Results</div>
          <div
            v-for="(stock, index) in suggestions"
            :key="stock.symbol"
            :class="[
              'suggestion-item',
              { highlighted: highlightedIndex === index },
            ]"
            @mousedown="selectStock(stock)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="stock-info">
              <div class="stock-main">
                <span class="stock-symbol">{{ stock.symbol }}</span>
                <span class="stock-name">{{ stock.name }}</span>
              </div>
              <div class="stock-meta">
                <span class="stock-exchange">{{ stock.exchange }}</span>
                <span class="stock-type">{{ stock.type }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- No Results -->
        <div
          v-else-if="searchQuery.trim() && !loading"
          class="suggestion-item no-results"
        >
          <font-awesome-icon icon="fa-solid fa-exclamation-circle" />
          <span>No stocks found for "{{ searchQuery }}"</span>
        </div>
      </div>
    </div>

    <!-- Recent Searches -->
    <div
      v-if="showRecentSearches && recentSearches.length > 0"
      class="recent-searches"
    >
      <div class="recent-header">Recent Searches</div>
      <div class="recent-items">
        <button
          v-for="stock in recentSearches"
          :key="stock.symbol"
          @click="selectStock(stock)"
          class="recent-item"
        >
          <span class="recent-symbol">{{ stock.symbol }}</span>
          <span class="recent-name">{{ stock.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { debounce } from "lodash";
import TradingViewSearchService from "@/services/TradingViewSearchService";
import {
  validateAndFormatSymbol,
  getPopularSymbols,
  SYMBOL_MAPPINGS,
} from "@/utils/symbolValidator";

// Props
const props = defineProps({
  placeholder: {
    type: String,
    default: "Search stocks by symbol or company name...",
  },
  modelValue: {
    type: String,
    default: "",
  },
  showRecentSearches: {
    type: Boolean,
    default: true,
  },
});

// Emits
const emit = defineEmits(["update:modelValue", "stock-selected", "search"]);

// Reactive data
const searchInput = ref(null);
const searchQuery = ref(props.modelValue);
const suggestions = ref([]);
const showSuggestions = ref(false);
const loading = ref(false);
const highlightedIndex = ref(-1);

// Helper function to get company names
const getCompanyNameFromSymbol = (symbol) => {
  const names = {
    AAPL: "Apple Inc.",
    GOOGL: "Alphabet Inc.",
    MSFT: "Microsoft Corporation",
    AMZN: "Amazon.com Inc.",
    META: "Meta Platforms Inc.",
    TSLA: "Tesla Inc.",
    NVDA: "NVIDIA Corporation",
    NFLX: "Netflix Inc.",
    JPM: "JPMorgan Chase & Co.",
    BAC: "Bank of America Corp.",
  };
  return names[symbol] || `${symbol} Corporation`;
};

// Popular stocks data (using validated symbols)
const popularStocks = ref(getPopularSymbols());
// Recent searches (stored in localStorage)
const recentSearches = ref([]);

// Computed
const maxSuggestions = 8;

// Methods
const handleInputCombined = () => {
  handleInputTest();
  handleInput();
};

const handleInputTest = () => {
  console.log("handleInputTest called - input is working!", searchQuery.value);
};

const handleInput = debounce(async () => {
  console.log("handleInput called with:", searchQuery.value); // Debug log
  emit("update:modelValue", searchQuery.value);

  if (searchQuery.value.trim().length < 2) {
    suggestions.value = [];
    showSuggestions.value = false;
    return;
  }

  loading.value = true;
  highlightedIndex.value = -1;
  showSuggestions.value = true; // Ensure suggestions are shown

  try {
    console.log("Calling searchStocks..."); // Debug log
    const results = await searchStocks(searchQuery.value);
    console.log("Search results received:", results); // Debug log
    suggestions.value = results.slice(0, maxSuggestions);
  } catch (error) {
    console.error("Error searching stocks:", error);
    suggestions.value = [];
  } finally {
    loading.value = false;
  }
}, 300);

const searchStocks = async (query) => {
  try {
    console.log("Searching for:", query); // Debug log

    // Use TradingView-compatible search service
    const results = await TradingViewSearchService.searchStocks(query);
    console.log("TradingView-compatible search results:", results);
    
    return results || [];

    // Approach 2: Try Finnhub API (alternative)
    // const finnhubKey = process.env.VUE_APP_FINNHUB_KEY;
    // if (finnhubKey) {
    //   const response = await fetch(`https://finnhub.io/api/v1/search?q=${query}&token=${finnhubKey}`);
    //   const data = await response.json();
    //   return data.result?.map(result => ({
    //     symbol: result.symbol,
    //     name: result.description,
    //     exchange: result.primary_exchange || 'NASDAQ',
    //     type: result.type || 'Stock',
    //     fullSymbol: `${result.primary_exchange || 'NASDAQ'}:${result.symbol}`
    //   })) || [];
    // }

    // Approach 3: Enhanced fallback with broader search
    console.log("Using enhanced fallback search for:", query);

    const enhancedStockList = [
      // Tech Giants
      {
        symbol: "AAPL",
        name: "Apple Inc.",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:AAPL",
      },
      {
        symbol: "GOOGL",
        name: "Alphabet Inc. Class A",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:GOOGL",
      },
      {
        symbol: "GOOG",
        name: "Alphabet Inc. Class C",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:GOOG",
      },
      {
        symbol: "MSFT",
        name: "Microsoft Corporation",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:MSFT",
      },
      {
        symbol: "AMZN",
        name: "Amazon.com Inc.",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:AMZN",
      },
      {
        symbol: "TSLA",
        name: "Tesla Inc.",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:TSLA",
      },
      {
        symbol: "NVDA",
        name: "NVIDIA Corporation",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:NVDA",
      },
      {
        symbol: "META",
        name: "Meta Platforms Inc.",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:META",
      },
      {
        symbol: "NFLX",
        name: "Netflix Inc.",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:NFLX",
      },
      {
        symbol: "AMD",
        name: "Advanced Micro Devices Inc.",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:AMD",
      },
      {
        symbol: "INTC",
        name: "Intel Corporation",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:INTC",
      },

      // Financial
      {
        symbol: "JPM",
        name: "JPMorgan Chase & Co.",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:JPM",
      },
      {
        symbol: "BAC",
        name: "Bank of America Corp.",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:BAC",
      },
      {
        symbol: "WFC",
        name: "Wells Fargo & Company",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:WFC",
      },
      {
        symbol: "GS",
        name: "Goldman Sachs Group Inc.",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:GS",
      },

      // Healthcare
      {
        symbol: "JNJ",
        name: "Johnson & Johnson",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:JNJ",
      },
      {
        symbol: "PFE",
        name: "Pfizer Inc.",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:PFE",
      },
      {
        symbol: "UNH",
        name: "UnitedHealth Group Inc.",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:UNH",
      },

      // Energy
      {
        symbol: "XOM",
        name: "Exxon Mobil Corporation",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:XOM",
      },
      {
        symbol: "CVX",
        name: "Chevron Corporation",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:CVX",
      },

      // Consumer
      {
        symbol: "KO",
        name: "Coca-Cola Company",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:KO",
      },
      {
        symbol: "PEP",
        name: "PepsiCo Inc.",
        exchange: "NASDAQ",
        type: "Common Stock",
        fullSymbol: "NASDAQ:PEP",
      },
      {
        symbol: "WMT",
        name: "Walmart Inc.",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:WMT",
      },
      {
        symbol: "DIS",
        name: "Walt Disney Company",
        exchange: "NYSE",
        type: "Common Stock",
        fullSymbol: "NYSE:DIS",
      },

      // Index ETFs
      {
        symbol: "SPY",
        name: "SPDR S&P 500 ETF Trust",
        exchange: "NYSE",
        type: "ETF",
        fullSymbol: "NYSE:SPY",
      },
      {
        symbol: "QQQ",
        name: "Invesco QQQ Trust",
        exchange: "NASDAQ",
        type: "ETF",
        fullSymbol: "NASDAQ:QQQ",
      },
      {
        symbol: "VTI",
        name: "Vanguard Total Stock Market ETF",
        exchange: "NYSE",
        type: "ETF",
        fullSymbol: "NYSE:VTI",
      },
    ];

    const filtered = enhancedStockList.filter(
      (stock) =>
        stock.symbol.toLowerCase().includes(query.toLowerCase()) ||
        stock.name.toLowerCase().includes(query.toLowerCase())
    );

    console.log("Search results:", filtered);
    return filtered;
  } catch (error) {
    console.error("Error in searchStocks:", error);
    return [];
  }
};

const selectStock = (stock) => {
  console.log("selectStock called with:", stock); // Debug log

  // Validate stock object
  if (!stock || !stock.symbol) {
    console.error("Invalid stock object:", stock);
    return;
  }

  searchQuery.value = stock.symbol;
  showSuggestions.value = false;
  highlightedIndex.value = -1;

  // Add to recent searches
  addToRecentSearches(stock);

  // Create enhanced stock object with full symbol for TradingView
  // Ensure we have proper exchange prefix for TradingView
  const exchange = stock.exchange || "NASDAQ";
  const fullSymbol = `${stock.symbol}`;

  const enhancedStock = {
    ...stock,
    symbol: fullSymbol, // Use full symbol (e.g., "NASDAQ:AAPL") for TradingView
    displaySymbol: stock.symbol, // Keep original symbol for display
    exchange: exchange,
    fullSymbol: fullSymbol,
  };

  console.log("Emitting stock-selected with:", enhancedStock); // Debug log
  // Emit events
  emit("update:modelValue", stock.symbol); // Display symbol for input
  emit("stock-selected", enhancedStock); // Enhanced symbol for TradingView
  emit("search", stock.symbol);
};

const clearSearch = () => {
  searchQuery.value = "";
  suggestions.value = [];
  showSuggestions.value = false;
  emit("update:modelValue", "");
};

const handleBlur = () => {
  // Delay hiding suggestions to allow for click events
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
};

const handleKeydown = (event) => {
  if (!showSuggestions.value) return;

  const totalItems = searchQuery.value.trim()
    ? suggestions.value.length
    : popularStocks.value.length;

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        totalItems - 1
      );
      break;
    case "ArrowUp":
      event.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
      break;
    case "Enter":
      event.preventDefault();
      if (highlightedIndex.value >= 0) {
        const selectedStock = searchQuery.value.trim()
          ? suggestions.value[highlightedIndex.value]
          : popularStocks.value[highlightedIndex.value];
        if (selectedStock) {
          selectStock(selectedStock);
        }
      } else if (suggestions.value.length > 0) {
        // Auto-select first suggestion if none is highlighted
        console.log("Auto-selecting first suggestion:", suggestions.value[0]); // Debug log
        selectStock(suggestions.value[0]);
      } else if (searchQuery.value.trim().length >= 2) {
        // Try to find exact match in popular stocks
        const exactMatch = popularStocks.value.find(
          (stock) =>
            stock.symbol.toLowerCase() ===
            searchQuery.value.trim().toLowerCase()
        );
        if (exactMatch) {
          console.log("Found exact match in popular stocks:", exactMatch); // Debug log
          selectStock(exactMatch);
        }
      }
      break;
    case "Escape":
      showSuggestions.value = false;
      searchInput.value?.blur();
      break;
  }
};

// Utility functions
const formatPrice = (price) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

const formatChange = (change) => {
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change.toFixed(2)}`;
};

const getChangeClass = (change) => {
  return {
    positive: change > 0,
    negative: change < 0,
    neutral: change === 0,
  };
};

// Recent searches management
const loadRecentSearches = () => {
  try {
    const saved = localStorage.getItem("stockSearchRecents");
    if (saved) {
      recentSearches.value = JSON.parse(saved);
    }
  } catch (error) {
    console.error("Error loading recent searches:", error);
  }
};

const addToRecentSearches = (stock) => {
  // Remove if already exists
  recentSearches.value = recentSearches.value.filter(
    (item) => item.symbol !== stock.symbol
  );

  // Add to beginning
  recentSearches.value.unshift(stock);

  // Keep only last 5
  recentSearches.value = recentSearches.value.slice(0, 5);

  // Save to localStorage
  try {
    localStorage.setItem(
      "stockSearchRecents",
      JSON.stringify(recentSearches.value)
    );
  } catch (error) {
    console.error("Error saving recent searches:", error);
  }
};

// Lifecycle
onMounted(() => {
  loadRecentSearches();
});

// Cleanup
onUnmounted(() => {
  // Any cleanup if needed
});
</script>

<style scoped>
.stock-search-container {
  position: relative;
  width: 100%;
}

.search-wrapper {
  position: relative;
}

.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #64748b;
  font-size: 14px;
  z-index: 2;
}

.stock-search-input {
  width: 100%;
  padding: 12px 40px 12px 36px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.stock-search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.clear-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 2;
}

.clear-btn:hover {
  background: #f1f5f9;
  color: #334155;
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
  margin-top: 4px;
}

.suggestions-section {
  padding: 8px 0;
}

.section-header {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 4px;
}

.suggestion-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f8fafc;
}

.suggestion-item:hover,
.suggestion-item.highlighted {
  background: #f8fafc;
}

.suggestion-item.loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #64748b;
  cursor: default;
}

.suggestion-item.no-results {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #64748b;
  cursor: default;
  font-style: italic;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.stock-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.stock-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.stock-symbol {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.stock-name {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.stock-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.stock-price {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.stock-change {
  font-size: 12px;
  font-weight: 500;
}

.stock-change.positive {
  color: #059669;
}

.stock-change.negative {
  color: #dc2626;
}

.stock-change.neutral {
  color: #64748b;
}

.stock-exchange,
.stock-type {
  font-size: 11px;
  color: #94a3b8;
}

.recent-searches {
  margin-top: 16px;
}

.recent-header {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.recent-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recent-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recent-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.recent-symbol {
  font-weight: 600;
  color: #1e293b;
}

.recent-name {
  color: #64748b;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .stock-search-input {
    padding: 10px 36px 10px 32px;
    font-size: 16px; /* Prevents zoom on iOS */
  }

  .suggestions-dropdown {
    max-height: 300px;
  }

  .stock-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .stock-meta {
    align-items: flex-start;
    flex-direction: row;
    gap: 12px;
  }
}
</style>
